import { ComponentProps, useCallback, useState } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { Radio, RadioGroup, Stack } from 'src/core/ui';
import { Button } from 'src/core/ui/Button';
import { ContentId } from 'src/utils/constants/contentId';

export type DeleteMarketplacePaymentDialogProps = Omit<
  ComponentProps<typeof GenericDialog>,
  'header' | 'footer'
> & {
  onDelete: (keepSaleLineItemLinkedWithSale: boolean) => void;
  onCancel: () => void;
  isLoading?: boolean;
  paymentId: number;
};

enum SaleLineItemOptions {
  Keep = 'Keep',
  Delete = 'Delete',
}

const SALE_LINE_ITEM_RETAIN_OPTIONS = [
  {
    value: SaleLineItemOptions.Delete,
    children: <Content id={ContentId.RemoveSaleChargesAndCredit} />,
  },
  {
    value: SaleLineItemOptions.Keep,
    children: <Content id={ContentId.LeaveAllSaleChargesAndCredit} />,
  },
];

export const DeleteMarketplacePaymentDialog = ({
  onDelete,
  onCancel,
  isLoading,
  paymentId,
  ...dialogProps
}: DeleteMarketplacePaymentDialogProps) => {
  const [keepSaleLineItemLinked, setKeepSaleLineItemLinked] = useState(
    SaleLineItemOptions.Keep
  );

  const handleSubmit = useCallback(() => {
    onDelete(keepSaleLineItemLinked == SaleLineItemOptions.Keep);
  }, [keepSaleLineItemLinked, onDelete]);

  return (
    <GenericDialog
      {...dialogProps}
      size="md"
      header={<Content id={ContentId.DeleteMarketplacePayment} />}
      footer={
        <Stack direction="row" gap="s">
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            <Content id={ContentId.Cancel} />
          </Button>
          <Button variant="regular" onClick={handleSubmit} disabled={isLoading}>
            <Content id={ContentId.Delete} />
          </Button>
        </Stack>
      }
      onCancel={onCancel}
    >
      <Stack direction="column" gap="l">
        <div>
          <Content id={ContentId.DeleteMarketplacePaymentSaleLineItemMessage} />
        </div>

        <RadioGroup
          value={keepSaleLineItemLinked}
          defaultValue={SaleLineItemOptions.Keep}
          onValueChange={(val: string) => {
            setKeepSaleLineItemLinked(val as SaleLineItemOptions);
          }}
          disabled={isLoading}
        >
          <Stack direction="column" gap="m">
            {SALE_LINE_ITEM_RETAIN_OPTIONS.map((m) => (
              <Radio
                key={m.value}
                value={m.value}
                label={m.children}
                disabled={isLoading}
              />
            ))}
          </Stack>
        </RadioGroup>
      </Stack>
    </GenericDialog>
  );
};
